// Admin: define / edit a customer's north-star goal as a list of criteria
// (metrics). Each metric is either a growth target (cumulative: start → target
// over the timeframe) or a recurring quota (e.g. 1 Podcast/Monat, 3 Posts/Woche).
// Monthly targets are derived automatically in code — no manual grid.
import { useState } from 'react';
import { useData } from './DataContext.jsx';
import { Modal } from './components.jsx';
import { IconX, IconFlag, IconPlus, IconTrend } from './icons.jsx';
import { METRIC_TYPE, CADENCE, RAMP } from './lib/goals.js';
import { todayIso } from './lib/format.js';

function emptyMetric(type = 'cumulative') {
  return { id: null, label: '', unit: '', type, start_value: '0', target_value: '', ramp: 'linear', cadence: 'monthly', quota: '1' };
}

function metricValid(m) {
  if (!m.label.trim()) return false;
  if (m.type === 'recurring') return (parseFloat(m.quota) || 0) > 0;
  return m.target_value !== '' && !Number.isNaN(parseFloat(m.target_value));
}

function GoalEditorModal({ customer, goal, onClose, onSaved }) {
  const { metricsByGoal, upsertGoal, saveMetrics, deleteGoal } = useData();

  const isEdit = !!goal;
  const [title, setTitle] = useState(goal?.title || '');
  const [startDate, setStartDate] = useState(goal?.start_date || todayIso());
  const [targetDate, setTargetDate] = useState(goal?.target_date || '');

  const [list, setList] = useState(() => {
    const existing = goal ? (metricsByGoal[goal.id] || []) : [];
    if (existing.length === 0) return [emptyMetric()];
    return existing.map(m => ({
      id: m.id,
      label: m.label || '',
      unit: m.unit || '',
      type: m.type || 'cumulative',
      start_value: m.start_value != null ? String(m.start_value) : '0',
      target_value: m.target_value != null ? String(m.target_value) : '',
      ramp: m.ramp || 'linear',
      cadence: m.cadence || 'monthly',
      quota: m.quota != null ? String(m.quota) : '1',
    }));
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState(null);

  const datesOk = startDate && targetDate && targetDate >= startDate;
  const hasCumulative = list.some(m => m.type === 'cumulative');
  const metricsOk = list.length > 0 && list.every(metricValid);
  // Timeframe is only required when at least one cumulative metric needs the ramp.
  const canSave = title.trim().length >= 3 && metricsOk && (!hasCumulative || datesOk) && !saving;

  function update(i, patch) {
    setList(xs => xs.map((m, j) => j === i ? { ...m, ...patch } : m));
  }
  function addMetric() { setList(xs => [...xs, emptyMetric()]); }
  function removeMetric(i) { setList(xs => xs.length > 1 ? xs.filter((_, j) => j !== i) : xs); }

  async function save() {
    if (!canSave) return;
    setSaving(true); setErr(null);
    try {
      // Cumulative metrics need a timeframe; if none given (recurring-only goal),
      // fall back to start=today, target=+1 year so the schema stays valid.
      const sd = startDate || todayIso();
      const td = targetDate || (() => { const d = new Date(sd); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); })();
      const saved = await upsertGoal(customer.id, { title: title.trim(), start_date: sd, target_date: td });
      await saveMetrics(saved.id, list);
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setErr(e.message || 'Fehler beim Speichern');
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    if (!window.confirm(`Ziel „${goal.title}" wirklich entfernen? Alle Kriterien, Fortschritte und Check-ins dazu werden gelöscht.`)) return;
    setDeleting(true); setErr(null);
    try {
      await deleteGoal(goal.id);
      onClose();
    } catch (e) {
      setErr(e.message || 'Fehler beim Löschen');
      setDeleting(false);
    }
  }

  return (
    <Modal onClose={saving || deleting ? () => {} : onClose}>
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--stroke)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconFlag size={12}/> {isEdit ? 'Kundenziel bearbeiten' : 'Kundenziel festlegen'}
          </div>
          <h2 className="serif" style={{ fontSize: 22, fontWeight: 500, margin: '4px 0 0', letterSpacing: '-0.01em' }}>
            {customer?.name}
          </h2>
        </div>
        <button className="icon-btn" onClick={onClose} disabled={saving || deleting}><IconX size={14}/></button>
      </div>

      <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
        <div className="field" style={{ marginBottom: 16 }}>
          <label>Übergeordnetes Ziel *</label>
          <input className="input" value={title} onChange={e => setTitle(e.target.value)}
                 placeholder="z. B. Reichweite & Content-Schlagzahl für die Stadt Coburg"/>
        </div>

        <div className="grid grid-2" style={{ gap: 16, marginBottom: 8 }}>
          <div className="field">
            <label>Start {hasCumulative && '*'}</label>
            <input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}/>
          </div>
          <div className="field">
            <label>Zieltermin {hasCumulative && '*'}</label>
            <input className="input" type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)}/>
            {hasCumulative && !datesOk && targetDate && (
              <div className="tiny" style={{ color: 'var(--crit)', marginTop: 4 }}>Zieltermin muss nach dem Start liegen.</div>
            )}
          </div>
        </div>
        <div className="tiny muted" style={{ marginBottom: 18 }}>
          Der Zeitraum gilt für Wachstumsziele (linearer Verlauf). Wiederkehrende Kriterien laufen monatlich.
        </div>

        {/* Criteria */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconTrend size={12}/> Kriterien · {list.length}
          </div>
          <button className="btn btn-ghost" style={{ padding: '4px 10px' }} onClick={addMetric}>
            <IconPlus size={13}/> Kriterium
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {list.map((m, i) => (
            <div key={i} style={{ padding: 14, background: 'var(--bg-sunken)', border: '1px solid var(--stroke)', borderRadius: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <div className="seg">
                  {Object.entries(METRIC_TYPE).map(([k, meta]) => (
                    <button key={k} className={m.type === k ? 'on' : ''} onClick={() => update(i, { type: k })}>{meta.label}</button>
                  ))}
                </div>
                <div style={{ flex: 1 }}/>
                {list.length > 1 && (
                  <button className="btn btn-ghost" style={{ padding: '4px 8px', color: 'var(--ink-3)' }} onClick={() => removeMetric(i)} title="Kriterium entfernen">
                    <IconX size={13}/>
                  </button>
                )}
              </div>

              <div className="grid grid-2" style={{ gap: 10 }}>
                <div className="field" style={{ gridColumn: 'span 2' }}>
                  <label>Bezeichnung *</label>
                  <input className="input" value={m.label} onChange={e => update(i, { label: e.target.value })}
                         placeholder={m.type === 'recurring' ? 'z. B. Podcast-Folge' : 'z. B. Follower (IG + FB)'}/>
                </div>

                {m.type === 'cumulative' ? (
                  <>
                    <div className="field">
                      <label>Startwert (heute)</label>
                      <input className="input" type="number" value={m.start_value} onChange={e => update(i, { start_value: e.target.value })} placeholder="z. B. 5000"/>
                    </div>
                    <div className="field">
                      <label>Zielwert *</label>
                      <input className="input" type="number" value={m.target_value} onChange={e => update(i, { target_value: e.target.value })} placeholder="z. B. 20000"/>
                    </div>
                    <div className="field">
                      <label>Einheit <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label>
                      <input className="input" value={m.unit} onChange={e => update(i, { unit: e.target.value })} placeholder="z. B. Follower"/>
                    </div>
                    <div className="field" style={{ gridColumn: 'span 2' }}>
                      <label>Verlauf</label>
                      <div className="seg">
                        {Object.entries(RAMP).map(([k, meta]) => (
                          <button key={k} className={(m.ramp || 'linear') === k ? 'on' : ''} onClick={() => update(i, { ramp: k })}>{meta.label}</button>
                        ))}
                      </div>
                      <div className="tiny muted" style={{ marginTop: 4 }}>{RAMP[m.ramp || 'linear'].hint}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="field">
                      <label>Anzahl *</label>
                      <input className="input" type="number" min="0" value={m.quota} onChange={e => update(i, { quota: e.target.value })} placeholder="z. B. 1"/>
                    </div>
                    <div className="field">
                      <label>Takt</label>
                      <select className="select" value={m.cadence} onChange={e => update(i, { cadence: e.target.value })}>
                        {Object.entries(CADENCE).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="field">
                      <label>Einheit <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label>
                      <input className="input" value={m.unit} onChange={e => update(i, { unit: e.target.value })} placeholder="z. B. Folgen"/>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {err && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 14 }}>{err}</div>}
      </div>

      <div style={{ padding: '14px 24px', borderTop: '1px solid var(--stroke)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        {isEdit ? (
          <button className="btn" onClick={handleDelete} disabled={saving || deleting}
                  style={{ color: 'var(--crit)', borderColor: 'rgba(239,68,68,0.3)' }}>
            {deleting ? 'Entferne…' : 'Ziel entfernen'}
          </button>
        ) : <span/>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving || deleting}>Abbrechen</button>
          <button className="btn btn-primary" onClick={save} disabled={!canSave || deleting}
                  style={{ opacity: (canSave && !deleting) ? 1 : 0.45 }}>
            {saving ? 'Speichere…' : isEdit ? 'Ziel speichern' : 'Ziel festlegen'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export { GoalEditorModal };
